// atoms
import { selectedGroupingScheduleAtom } from '../../atoms';

// components
import { Button, Menu, MenuItem } from '@mui/material';

// enums
import { View, ViewContext } from '../../enums';

// hooks
import { useState } from 'react';
import { useNavParams, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';

// types
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

// utils
import { getViewContext } from '../../utils';
import { Icon } from '@sdb/planning-ui';

type Props = {
  view: View;
  handleChangeView: (view: View) => void;
  disabled?: boolean;
};

const viewIcon: { [T in View]: IconType } = {
  [View.Roster]: 'grid-2',
  [View.RosterPragmatic]: 'grid-2',
  [View.Timeline]: 'timeline-arrow',
  [View.List]: 'bars',
  [View.Cards]: 'grid-list',
  [View.CardsPragmatic]: 'grid-list',
  [View.Map]: 'map-location-dot'
};

export default function ViewSwitch({
  view,
  handleChangeView,
  disabled
}: Props) {
  const { t } = useTranslation('common');
  const {
    hasActivityPlanningViewAccess,
    hasWorkPlacePlanningViewAccess,
    hasMyActivitiesProjectAccess,
    hasManageOwnProjectsAccess,
    hasKidsPlanAccess,
    hasActivityPlanningDndKitAccess,
    hasActivityPlanningPragmaticAccess,
    hasActivityPlanningRosterDndKitAccess,
    hasActivityPlanningRosterPragmaticAccess
  } = useSelector(({ grants }) => grants);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [, setSelectedGroupingSchedule] = useRecoilState(
    selectedGroupingScheduleAtom
  );
  const { params, setParams } = useNavParams();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const open = Boolean(anchorEl);

  let views: View[] = [];
  if (
    hasActivityPlanningViewAccess ||
    hasWorkPlacePlanningViewAccess ||
    hasMyActivitiesProjectAccess ||
    hasManageOwnProjectsAccess ||
    hasKidsPlanAccess
  ) {
    // The legacy dnd-kit Roster view and the new Pragmatic Roster view are each
    // gated by their own org-level feature toggle so tenants can enable either
    // or both during the migration period.
    if (hasActivityPlanningRosterDndKitAccess) {
      views.push(View.Roster);
    }
    if (hasActivityPlanningRosterPragmaticAccess) {
      views.push(View.RosterPragmatic);
    }
  }
  if (
    hasActivityPlanningViewAccess ||
    hasWorkPlacePlanningViewAccess ||
    hasMyActivitiesProjectAccess ||
    hasManageOwnProjectsAccess
  ) {
    views.push(View.List);
  }
  if (hasActivityPlanningViewAccess) {
    views.push(View.Timeline);
    // The legacy dnd-kit Cards view and the new Pragmatic Cards view are each
    // gated by their own org-level feature toggle so tenants can enable either
    // or both during the migration period.
    if (hasActivityPlanningDndKitAccess) {
      views.push(View.Cards);
    }
    if (hasActivityPlanningPragmaticAccess) {
      views.push(View.CardsPragmatic);
    }
    views.push(View.Map);
  }

  // Only Roster and List view when viewing My Activities
  if (getViewContext() === ViewContext.Own) {
    views = views.filter(
      (view: View) =>
        view === View.List ||
        view === View.Roster ||
        view === View.RosterPragmatic
    );
  }

  if (!views.includes(view)) {
    setParams({ ...params, view: views[0] });
    handleChangeView(views[0]);
  }

  return (
    <div>
      <Button
        variant="outlined"
        disabled={disabled}
        sx={{
          display: 'flex',
          gap: (theme) => theme.spacing(2)
        }}
        onClick={handleClick}
        startIcon={<Icon type={viewIcon[view]} />}
        endIcon={<Icon type="chevron-down" fontSize="small" />}
      >
        {t(`activityPlanning.view.${view}`)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        {views.map((v) => (
          <MenuItem
            key={v}
            color="neutral"
            onClick={() => {
              setParams({ ...params, view: v });
              handleChangeView(v);
              if (v !== View.Map) {
                setSelectedGroupingSchedule('');
              }
              setAnchorEl(null);
            }}
          >
            <Icon type={viewIcon[v]} sx={{ mr: 1.5 }} fontSize="small" />
            {t(`activityPlanning.view.${v}`)}
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
}
